import { useState, useEffect, useCallback } from "react";
import { saveToIndexedDB, loadFromIndexedDB } from "../../../util/indexedDBUtils";

/**
 * useStepPersistence - Custom hook for managing step form data persistence
 * 
 * Handles:
 * - Restoring data from localStorage (with legacy key support)
 * - Restoring data from IndexedDB as fallback
 * - Auto-saving to IndexedDB
 * - Clearing storage after successful save
 * - Tracking unsaved changes
 * 
 * @param {Object} config
 * @param {string} config.mode - 'add' or 'edit'
 * @param {string} config.stepId - Step ID (for edit mode)
 * @param {string} config.shortDescription - Current short description
 * @param {Array} config.stepDetails - Current step details
 * @param {Array} config.bomTable - Current BOM table
 * @param {string} config.bomTableCategory - BOM table category name
 * @param {string} config.bomTableCategoryId - BOM table category ID
 * @param {string} config.partNumberCategory - Part number category name
 * @param {string} config.partTableCategoryId - Part number category ID
 * @param {boolean} config.isHideStepHeading - Hide step heading flag
 * @param {boolean} config.isHideStepDetailsHeading - Hide step details flag
 * @param {boolean} config.isHideBomHeading - Hide BOM table flag
 * @param {function} config.setShortDescription - Setter for short description
 * @param {function} config.setStepDetails - Setter for step details
 * @param {function} config.setBomTable - Setter for BOM table
 * @param {function} config.setBomTableCategory - Setter for BOM category
 * @param {function} config.setBomTableCategoryId - Setter for BOM category ID
 * @param {function} config.setPartNumberCategory - Setter for part category
 * @param {function} config.setPartTableCategoryId - Setter for part category ID
 * @param {function} config.setIsHideStepHeading - Setter for hide step heading
 * @param {function} config.setIsHideStepDetailsHeading - Setter for hide step details
 * @param {function} config.setIsHideBomHeading - Setter for hide BOM table
 * 
 * @returns {Object} Persistence state and handlers
 */
const useStepPersistence = ({
  mode,
  stepId,
  shortDescription,
  stepDetails,
  bomTable,
  bomTableCategory,
  bomTableCategoryId,
  partNumberCategory,
  partTableCategoryId,
  isHideStepHeading,
  isHideStepDetailsHeading,
  isHideBomHeading,
  setShortDescription,
  setStepDetails,
  setBomTable,
  setBomTableCategory,
  setBomTableCategoryId,
  setPartNumberCategory,
  setPartTableCategoryId,
  setIsHideStepHeading,
  setIsHideStepDetailsHeading,
  setIsHideBomHeading,
}) => {
  const [localStorageRestored, setLocalStorageRestored] = useState(false);
  const [hasLocalStorageData, setHasLocalStorageData] = useState(false);

  /**
   * Get localStorage key with support for legacy keys
   */
  const getWithLegacyFallback = useCallback((camelKey, legacyKey) => {
    const v = localStorage.getItem(camelKey);
    if (v !== null && v !== undefined) return v;
    return localStorage.getItem(legacyKey);
  }, []);

  /**
   * Restore data from localStorage (edit mode)
   */
  useEffect(() => {
    if (mode !== "edit") return;

    const savedShortDescription = getWithLegacyFallback("editStepShortDescription", "editstepShortDescription");
    const savedStepDetails = getWithLegacyFallback("editStepDetails", "editstepDetails");
    const savedBomTable = getWithLegacyFallback("editBomTable", "editbomTable");
    const savedBomTableCategory = getWithLegacyFallback("editBomTableCategory", "editbomTableCategory");
    const savedBomTableCategoryId = getWithLegacyFallback("editBomTableCategoryId", "editbomTableCategoryId");
    const savedPartNumberCategory = getWithLegacyFallback("editPartNumberCategory", "editpartNumberCategory");
    const savedPartTableCategoryId = getWithLegacyFallback("editPartTableCategoryId", "editpartTableCategoryId");
    const savedIsHideStepHeading = getWithLegacyFallback("editIsHideStepHeading", "editisHideStepHeading");
    const savedIsHideStepDetailsHeading = getWithLegacyFallback("editIsHideStepDetailsHeading", "editisHideStepDetailsHeading");
    const savedIsHideBomHeading = getWithLegacyFallback("editIsHideBomHeading", "editisHideBomHeading");

    const isReturningFromPreview =
      (savedShortDescription && savedShortDescription !== "undefined") ||
      (savedStepDetails && savedStepDetails !== "undefined") ||
      (savedBomTable && savedBomTable !== "undefined");

    if (isReturningFromPreview) {
      setHasLocalStorageData(true);

      // Restore short description
      if (savedShortDescription && savedShortDescription !== "undefined") {
        setShortDescription(savedShortDescription);
      }

      // Restore step details
      if (savedStepDetails && savedStepDetails !== "undefined") {
        try {
          setStepDetails(JSON.parse(savedStepDetails));
        } catch (e) {
          console.warn("Failed to parse saved step details:", e);
        }
      }

      // Restore BOM table
      if (savedBomTable && savedBomTable !== "undefined") {
        try {
          setBomTable(JSON.parse(savedBomTable));
        } catch (e) {
          console.warn("Failed to parse saved BOM table:", e);
        }
      }

      // Restore category data
      if (savedBomTableCategory && savedBomTableCategory !== "undefined") {
        setBomTableCategory(savedBomTableCategory);
      }
      if (savedBomTableCategoryId && savedBomTableCategoryId !== "undefined") {
        setBomTableCategoryId(savedBomTableCategoryId);
      }
      if (savedPartNumberCategory && savedPartNumberCategory !== "undefined") {
        setPartNumberCategory(savedPartNumberCategory);
      }
      if (savedPartTableCategoryId && savedPartTableCategoryId !== "undefined") {
        setPartTableCategoryId(savedPartTableCategoryId);
      }

      // Restore checkbox states
      if (savedIsHideStepHeading && savedIsHideStepHeading !== "undefined") {
        try {
          setIsHideStepHeading(JSON.parse(savedIsHideStepHeading));
        } catch (e) {
          console.warn("Failed to parse saved hide step heading state:", e);
        }
      }
      if (savedIsHideStepDetailsHeading && savedIsHideStepDetailsHeading !== "undefined") {
        try {
          setIsHideStepDetailsHeading(JSON.parse(savedIsHideStepDetailsHeading));
        } catch (e) {
          console.warn("Failed to parse saved hide step details heading state:", e);
        }
      }
      if (savedIsHideBomHeading && savedIsHideBomHeading !== "undefined") {
        try {
          setIsHideBomHeading(JSON.parse(savedIsHideBomHeading));
        } catch (e) {
          console.warn("Failed to parse saved hide BOM heading state:", e);
        }
      }

      // Clear localStorage after restoration
      [
        "editStepShortDescription", "editStepDetails", "editBomTable",
        "editBomTableCategory", "editBomTableCategoryId",
        "editPartNumberCategory", "editPartTableCategoryId",
        "editIsHideStepHeading", "editIsHideStepDetailsHeading", "editIsHideBomHeading",
        // Legacy keys
        "editstepShortDescription", "editstepDetails", "editbomTable",
        "editbomTableCategory", "editbomTableCategoryId",
        "editpartNumberCategory", "editpartTableCategoryId",
        "editisHideStepHeading", "editisHideStepDetailsHeading", "editisHideBomHeading",
      ].forEach((k) => localStorage.removeItem(k));
    } else {
      // Fallback: restore from IndexedDB
      const restoreFromIndexedDB = async () => {
        try {
          const draftId = `step:${stepId || "new"}`;
          const record = await loadFromIndexedDB("stepDrafts", draftId);
          if (record && record.data) {
            const d = record.data;
            if (d.shortDescription) setShortDescription(d.shortDescription);
            if (Array.isArray(d.stepDetails)) setStepDetails(d.stepDetails);
            if (Array.isArray(d.bomTable)) setBomTable(d.bomTable);
            if (typeof d.bomTableCategory === "string") setBomTableCategory(d.bomTableCategory);
            if (typeof d.bomTableCategoryId === "string") setBomTableCategoryId(d.bomTableCategoryId);
            if (typeof d.partNumberCategory === "string") setPartNumberCategory(d.partNumberCategory);
            if (typeof d.partTableCategoryId === "string") setPartTableCategoryId(d.partTableCategoryId);
            if (typeof d.isHideStepHeading === "boolean") setIsHideStepHeading(d.isHideStepHeading);
            if (typeof d.isHideStepDetailsHeading === "boolean") setIsHideStepDetailsHeading(d.isHideStepDetailsHeading);
            if (typeof d.isHideBomHeading === "boolean") setIsHideBomHeading(d.isHideBomHeading);
          }
        } catch (e) {
          console.warn("IndexedDB restore failed:", e?.message || e);
        } finally {
          setHasLocalStorageData(false);
          setLocalStorageRestored(true);
        }
      };
      restoreFromIndexedDB();
      return;
    }

    setLocalStorageRestored(true);
  }, [mode, getWithLegacyFallback, stepId]);

  /**
   * Restore data from localStorage (add mode)
   */
  useEffect(() => {
    if (mode !== "add") return;

    const savedShortDescription = localStorage.getItem("stepShortDescription");
    const savedStepDetails = localStorage.getItem("stepDetails");
    const savedBomTable = localStorage.getItem("bomTable");
    const savedBomTableCategory = localStorage.getItem("bomTableCategory");
    const savedBomTableCategoryId = localStorage.getItem("bomTableCategoryId");
    const savedPartNumberCategory = localStorage.getItem("partNumberCategory");
    const savedPartTableCategoryId = localStorage.getItem("partTableCategoryId");
    const savedIsHideStepHeading = localStorage.getItem("isHideStepHeading");
    const savedIsHideStepDetailsHeading = localStorage.getItem("isHideStepDetailsHeading");
    const savedIsHideBomHeading = localStorage.getItem("isHideBomHeading");

    const isReturningFromPreview = savedShortDescription && savedShortDescription !== "undefined";

    if (isReturningFromPreview) {
      // Restore short description
      if (savedShortDescription && savedShortDescription !== "undefined") {
        setShortDescription(savedShortDescription);
      }

      // Restore step details
      if (savedStepDetails && savedStepDetails !== "undefined") {
        try {
          setStepDetails(JSON.parse(savedStepDetails));
        } catch (e) {
          console.warn("Failed to parse saved step details:", e);
        }
      }

      // Restore BOM table
      if (savedBomTable && savedBomTable !== "undefined") {
        try {
          setBomTable(JSON.parse(savedBomTable));
        } catch (e) {
          console.warn("Failed to parse saved BOM table:", e);
        }
      }

      // Restore category data
      if (savedBomTableCategory && savedBomTableCategory !== "undefined") {
        setBomTableCategory(savedBomTableCategory);
      }
      if (savedBomTableCategoryId && savedBomTableCategoryId !== "undefined") {
        setBomTableCategoryId(savedBomTableCategoryId);
      }
      if (savedPartNumberCategory && savedPartNumberCategory !== "undefined") {
        setPartNumberCategory(savedPartNumberCategory);
      }
      if (savedPartTableCategoryId && savedPartTableCategoryId !== "undefined") {
        setPartTableCategoryId(savedPartTableCategoryId);
      }

      // Restore checkbox states
      if (savedIsHideStepHeading && savedIsHideStepHeading !== "undefined") {
        try {
          setIsHideStepHeading(JSON.parse(savedIsHideStepHeading));
        } catch (e) {
          console.warn("Failed to parse saved hide step heading state:", e);
        }
      }
      if (savedIsHideStepDetailsHeading && savedIsHideStepDetailsHeading !== "undefined") {
        try {
          setIsHideStepDetailsHeading(JSON.parse(savedIsHideStepDetailsHeading));
        } catch (e) {
          console.warn("Failed to parse saved hide step details heading state:", e);
        }
      }
      if (savedIsHideBomHeading && savedIsHideBomHeading !== "undefined") {
        try {
          setIsHideBomHeading(JSON.parse(savedIsHideBomHeading));
        } catch (e) {
          console.warn("Failed to parse saved hide BOM heading state:", e);
        }
      }

      // Clear localStorage after restoration
      [
        "stepShortDescription", "stepDetails", "bomTable",
        "bomTableCategory", "bomTableCategoryId",
        "partNumberCategory", "partTableCategoryId",
        "isHideStepHeading", "isHideStepDetailsHeading", "isHideBomHeading",
      ].forEach((k) => localStorage.removeItem(k));
    }

    setLocalStorageRestored(true);
  }, [mode]);

  /**
   * Auto-save to IndexedDB when data changes
   */
  useEffect(() => {
    if (!localStorageRestored) return;

    const draftId = `step:${stepId || "new"}`;
    const payload = {
      shortDescription,
      stepDetails,
      bomTable,
      bomTableCategory,
      bomTableCategoryId,
      partNumberCategory,
      partTableCategoryId,
      isHideStepHeading,
      isHideStepDetailsHeading,
      isHideBomHeading,
      updatedAt: Date.now(),
      mode,
    };

    saveToIndexedDB("stepDrafts", draftId, payload).catch((err) => {
      console.warn("IndexedDB save failed:", err);
    });
  }, [
    localStorageRestored,
    shortDescription,
    stepDetails,
    bomTable,
    bomTableCategory,
    bomTableCategoryId,
    partNumberCategory,
    partTableCategoryId,
    isHideStepHeading,
    isHideStepDetailsHeading,
    isHideBomHeading,
    mode,
    stepId,
  ]);

  /**
   * Save data to localStorage for preview navigation
   */
  const saveForPreview = () => {
    const getKey = (base) => {
      return mode === "edit" ? `edit${base}` : `${base.charAt(0).toLowerCase()}${base.slice(1)}`;
    };

    try {
      localStorage.setItem(getKey("StepShortDescription"), shortDescription);
      localStorage.setItem(getKey("StepDetails"), JSON.stringify(stepDetails));
      localStorage.setItem(getKey("BomTable"), JSON.stringify(bomTable));
      localStorage.setItem(getKey("BomTableCategory"), bomTableCategory);
      localStorage.setItem(getKey("BomTableCategoryId"), bomTableCategoryId);
      localStorage.setItem(getKey("PartNumberCategory"), partNumberCategory);
      localStorage.setItem(getKey("PartTableCategoryId"), partTableCategoryId);
      localStorage.setItem(getKey("IsHideStepHeading"), JSON.stringify(isHideStepHeading));
      localStorage.setItem(getKey("IsHideStepDetailsHeading"), JSON.stringify(isHideStepDetailsHeading));
      localStorage.setItem(getKey("IsHideBomHeading"), JSON.stringify(isHideBomHeading));

      // Backward compatibility: also write legacy lowercase edit-* keys
      if (mode === "edit") {
        localStorage.setItem("editstepShortDescription", shortDescription);
        localStorage.setItem("editstepDetails", JSON.stringify(stepDetails));
        localStorage.setItem("editbomTable", JSON.stringify(bomTable));
        localStorage.setItem("editbomTableCategory", bomTableCategory);
        localStorage.setItem("editbomTableCategoryId", bomTableCategoryId);
        localStorage.setItem("editpartNumberCategory", partNumberCategory);
        localStorage.setItem("editpartTableCategoryId", partTableCategoryId);
        localStorage.setItem("editisHideStepHeading", JSON.stringify(isHideStepHeading));
        localStorage.setItem("editisHideStepDetailsHeading", JSON.stringify(isHideStepDetailsHeading));
        localStorage.setItem("editisHideBomHeading", JSON.stringify(isHideBomHeading));
      }
    } catch (e) {
      console.warn("Failed to persist step draft for preview:", e);
    }
  };

  /**
   * Clear all stored data (after successful save)
   */
  const clearStoredData = () => {
    const draftId = `step:${stepId || "new"}`;
    
    // Clear IndexedDB
    try {
      const request = indexedDB.open("canvasElementsDB");
      request.onsuccess = () => {
        const db = request.result;
        if (db.objectStoreNames.contains("stepDrafts")) {
          const transaction = db.transaction(["stepDrafts"], "readwrite");
          const store = transaction.objectStore("stepDrafts");
          store.delete(draftId);
        }
      };
    } catch (e) {
      console.warn("Failed to clear IndexedDB:", e);
    }

    // Clear localStorage
    const keys = mode === "edit"
      ? [
          "editStepShortDescription", "editStepDetails", "editBomTable",
          "editBomTableCategory", "editBomTableCategoryId",
          "editPartNumberCategory", "editPartTableCategoryId",
          "editIsHideStepHeading", "editIsHideStepDetailsHeading", "editIsHideBomHeading",
          // Legacy keys
          "editstepShortDescription", "editstepDetails", "editbomTable",
          "editbomTableCategory", "editbomTableCategoryId",
          "editpartNumberCategory", "editpartTableCategoryId",
          "editisHideStepHeading", "editisHideStepDetailsHeading", "editisHideBomHeading",
        ]
      : [
          "stepShortDescription", "stepDetails", "bomTable",
          "bomTableCategory", "bomTableCategoryId",
          "partNumberCategory", "partTableCategoryId",
          "isHideStepHeading", "isHideStepDetailsHeading", "isHideBomHeading",
        ];

    keys.forEach((k) => localStorage.removeItem(k));
  };

  return {
    localStorageRestored,
    hasLocalStorageData,
    saveForPreview,
    clearStoredData,
  };
};

export default useStepPersistence;
